import type { ChartData } from "chart.js"

import { ApiTypes } from "types"

import { chartColors, type ChartContext, createGradient, noDataLabel } from "./chartConfig"

export const trendsDateRangeOptions = ["Last 7 days", "Last 14 days", "Last 30 days", "Last 90 days"]

export const dashboardDateRangeOptions = [
  "Last 7 days",
  "Last 14 days",
  "Last 30 days",
  "Last 90 days",
  "Last Year",
  "This Year",
]

export type TimeSeriesPoint = ApiTypes.Res.TimeSeriesPoint
export type TrendData = ApiTypes.Res.TrendData
export type EnterpriseChartType = "bar" | "doughnut" | "line"
export type EnterpriseChartData<TType extends EnterpriseChartType> = ChartData<TType, number[], string>
export type MiniTrendColumnData = {
  label: string
  value: string
  subValue?: string
  delta: string
  deltaType: "positive" | "negative"
  trendDays?: string
  chartData: number[]
  hideChart?: boolean
}

type LineSeriesConfig = {
  key: string
  label: string
  points: TimeSeriesPoint[]
  color: string
  fillColor?: string
}

type BarSeriesConfig = {
  key: string
  label: string
  points: TimeSeriesPoint[]
  color: string
}

export const emptyEnterpriseTotalStats: ApiTypes.Res.EnterpriseDashboardTotalsWidget = {
  total_contractors: 0,
  total_customers: 0,
  total_devices: 0,
  thermostats: 0,
  sensors: 0,
  cwa: 0,
  mobile_app: 0,
}

export const emptyTrendData: TrendData = {
  last_period: 0,
  prev_period: 0,
  date_data: [],
}

export const emptyThermostatTrend: ApiTypes.Res.InstallationDevicesTrendWidget = {
  instaled_devices: emptyTrendData,
  ordered_devices: emptyTrendData,
}

export const emptyThermostatCharts: ApiTypes.Res.EnterpriseDashboardThermostatChartsWidget = {
  installs_over_time: { series: [] },
  installed_vs_ordered_trend: { installed: [], ordered: [] },
  mobile_app_vs_customers: { customers: [], mobile_app: [] },
}

export const emptyOrderStatuses: ApiTypes.Res.EnterpriseDashboardOrderStatusesWidget = {
  active_orders: 0,
  new: 0,
  confirmed: 0,
  packed: 0,
  shipping: 0,
  fulfilled: 0,
  denied: 0,
}

export const emptyOrderCharts: ApiTypes.Res.EnterpriseDashboardOrderChartsWidget = {
  thermostats: [],
  cwa: [],
  ths: [],
  wls: [],
}

export const emptyAlertCharts: ApiTypes.Res.EnterpriseDashboardAlertChartsWidget = {
  alerts_over_time: {
    efficiency_issue: [],
    air_quality: [],
    fan_malfunction: [],
    ambient_sensor_malfunction: [],
    humidity_sensor_malfunction: [],
    temperature_sensor_malfunction: [],
  },
  alerts_statuses: {
    active: [],
    frozen: [],
    acknowledged: [],
  },
}

export const emptyPerftestCharts: ApiTypes.Res.EnterpriseDashboardPerftestChartsWidget = {
  pass: [],
  issue: [],
}

export const periodFromOption = (option: string): number | string => {
  if (option === "Last Year" || option === "This Year") return option

  const match = option.match(/\d+/)

  return match ? Number(match[0]) : 7
}

export const dayPeriodFromOption = (option: string): number => {
  const period = periodFromOption(option)

  return typeof period === "number" ? period : 7
}

export const formatNumber = (value: number | undefined) => (value ?? 0).toLocaleString()

const formatDateLabel = (date: string) => {
  if (!date || date === noDataLabel) return noDataLabel

  const parsedDate = new Date(`${date}T00:00:00`)

  if (Number.isNaN(parsedDate.getTime())) return date

  return parsedDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}

const getTimeSeriesLabels = (series: TimeSeriesPoint[][]) =>
  Array.from(new Set(series.flat().map(point => point.date))).sort((a, b) => a.localeCompare(b))

export const getSafeTimeSeriesLabels = (series: TimeSeriesPoint[][]) => {
  const labels = getTimeSeriesLabels(series)

  return labels.length > 0 ? labels : [noDataLabel]
}

export const getSeriesCounts = (labels: string[], series: TimeSeriesPoint[]): number[] => {
  const countsByDate = new Map(series.map(point => [point.date, point.count]))

  return labels.map(label => countsByDate.get(label) ?? 0)
}

export const getSeriesTotal = (series: TimeSeriesPoint[]) => series.reduce((total, point) => total + point.count, 0)

export const getPercent = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 100) : 0)

const getTrendPercentFromSeries = (series: number[]) => {
  const first = series[0] ?? 0
  const last = series.at(-1) ?? 0

  if (first === 0) return last > 0 ? 100 : 0

  return Math.round(Math.abs((last - first) / first) * 100)
}

const getTrendDirectionFromSeries = (series: number[]): "positive" | "negative" => {
  const first = series[0] ?? 0
  const last = series.at(-1) ?? 0

  return last < first ? "negative" : "positive"
}

export const buildPercentageSeries = (numerator: TimeSeriesPoint[], denominator: TimeSeriesPoint[]) => {
  const labels = getSafeTimeSeriesLabels([numerator, denominator])
  const numeratorCounts = getSeriesCounts(labels, numerator)
  const denominatorCounts = getSeriesCounts(labels, denominator)

  return numeratorCounts.map((count, index) => getPercent(count, denominatorCounts[index] ?? 0))
}

export const buildPassRateSeries = (pass: TimeSeriesPoint[], issue: TimeSeriesPoint[]) => {
  const labels = getSafeTimeSeriesLabels([pass, issue])
  const passCounts = getSeriesCounts(labels, pass)
  const issueCounts = getSeriesCounts(labels, issue)

  return passCounts.map((count, index) => getPercent(count, count + (issueCounts[index] ?? 0)))
}

export const buildLineChartData = (series: LineSeriesConfig[]): EnterpriseChartData<"line"> => {
  const labels = getSafeTimeSeriesLabels(series.map(item => item.points))

  return {
    labels: labels.map(formatDateLabel),
    datasets: series.map(item => ({
      label: item.label,
      data: getSeriesCounts(labels, item.points),
      borderColor: item.color,
      backgroundColor: (context: ChartContext) => createGradient(context, item.fillColor ?? item.color),
      borderWidth: 2,
      fill: true,
      pointRadius: 0,
      tension: 0.28,
    })),
  }
}

export const buildBarChartData = (series: BarSeriesConfig[]): EnterpriseChartData<"bar"> => {
  const labels = getSafeTimeSeriesLabels(series.map(item => item.points))

  return {
    labels: labels.map(formatDateLabel),
    datasets: series.map(item => ({
      label: item.label,
      data: getSeriesCounts(labels, item.points),
      backgroundColor: item.color,
      borderRadius: 0,
      barPercentage: 0.42,
      categoryPercentage: 0.48,
    })),
  }
}

export const buildDoughnutData = (
  statuses: ApiTypes.Res.EnterpriseDashboardOrderStatusesWidget,
): EnterpriseChartData<"doughnut"> => ({
  labels: ["New", "Fulfilled", "Confirmed", "Denied", "Packed", "Shipping"],
  datasets: [
    {
      data: [statuses.new, statuses.fulfilled, statuses.confirmed, statuses.denied, statuses.packed, statuses.shipping],
      backgroundColor: [
        chartColors.new,
        chartColors.fulfilled,
        chartColors.confirmed,
        chartColors.denied,
        chartColors.packed,
        chartColors.shipping,
      ],
      borderColor: "#fff",
      borderWidth: 5,
      hoverBorderColor: "#fff",
    },
  ],
})

export const buildTrendColumn = (label: string, data: TrendData, period: number): MiniTrendColumnData => {
  const diff = data.last_period - data.prev_period
  const percent =
    data.prev_period !== 0 ? Math.round(Math.abs(diff / data.prev_period) * 100) : data.last_period ? 100 : 0

  return {
    label,
    value: formatNumber(data.last_period),
    delta: `${percent}%`,
    deltaType: diff < 0 ? "negative" : "positive",
    chartData: data.date_data.length > 0 ? data.date_data.map(point => point.count) : [0],
    trendDays: `past ${period} days`,
  }
}

export const buildSeriesTrendColumn = ({
  label,
  period,
  series,
  subValue,
  value,
  hideChart,
}: {
  label: string
  period: number
  series: number[]
  subValue?: string
  value?: string
  hideChart?: boolean
}): MiniTrendColumnData => ({
  label,
  value: value ?? formatNumber(series.reduce((total, point) => total + point, 0)),
  subValue,
  delta: `${getTrendPercentFromSeries(series)}%`,
  deltaType: getTrendDirectionFromSeries(series),
  chartData: series,
  hideChart,
  trendDays: `past ${period} days`,
})
